/**
 * Utility for safely making API calls and handling errors
 * This wraps Supabase calls and other async operations to provide
 * consistent error handling and prevent app crashes on network failures
 */

import { supabase } from '../lib/supabase';

/**
 * Type for the result of a safe API call
 */
export interface SafeApiResult<T> {
  data: T | null;
  error: Error | null;
  isConnectionError: boolean;
}

/**
 * Check if Supabase client is properly initialized
 */
export const isSupabaseAvailable = (): boolean => {
  return !!supabase && typeof supabase.from === 'function';
};

/**
 * Safely execute a Supabase query
 * @param queryFn A function that makes a Supabase query
 * @returns Object with data, error, and connection status
 */
export const safeApiCall = async <T>(
  queryFn: () => Promise<T>
): Promise<SafeApiResult<T>> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase client not initialized');
    return {
      data: null, 
      error: new Error('Database connection unavailable'),
      isConnectionError: true
    };
  }

  try {
    const data = await queryFn();
    return { 
      data, 
      error: null,
      isConnectionError: false
    };
  } catch (error) {
    const isNetworkError = 
      error instanceof Error && 
      (error.message.includes('fetch failed') || 
       error.message.includes('Failed to fetch') ||
       error.message.includes('Network Error') ||
       error.message.includes('net::ERR_NAME_NOT_RESOLVED'));
    
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
      isConnectionError: isNetworkError
    };
  }
};

/**
 * Retries a function with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retries
 * @param delayMs Initial delay in milliseconds
 * @returns Result of the function call or throws an error after max retries
 */
export const withRetry = async <T>(
  fn: () => Promise<T>, 
  maxRetries: number = 3, 
  delayMs: number = 1000
): Promise<T> => {
  let lastError: unknown;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, i)));
    }
  }
  
  throw lastError;
};
